import {
    IonButton,
    IonCol,
    IonLabel,
    IonRow,
    IonSpinner,
    useIonToast,
    useIonViewDidEnter,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import imageCompression from 'browser-image-compression';
import { FormikProps } from 'formik';
import { useAtom } from 'jotai';
import React, { FC, useEffect, useMemo, useState } from 'react';
import ImageUploading, { ImageListType } from 'react-images-uploading';
import { ImageType } from 'react-images-uploading/dist/typings';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { IDeepomaticIssueOrTask } from '../../interfaces/IDeepomaticIssueOrTask';
import { IDeepomaticTaskNameAndValueForm } from '../../interfaces/IDeepomaticTaskNameAndValueForm';
import { IDeepomaticTaskWithOcr } from '../../interfaces/IDeepomaticTaskWithOcr';
import { DevicesUpdateFormValues } from '../../pages/installation/wire/Devices';
import {
    accessTokenAtom,
    jobIdAtom,
    photosListLoadableAtom,
    photosUploadedAtoms,
} from '../../store/Atoms';
import { uploadImageRequest } from '../../store/Requests';
import ImageInfo, {
    ImageCategories,
    ImageCategory,
} from '../../types/ImageInfo';
import { ROOT_URL } from '../../utils/constants';
import {
    convertHeicToJpeg,
    convertPngToJpeg,
} from '../../utils/convertFileToJpeg';
import { getErrorMessage } from '../../utils/errorMessage';
import DeepomaticModal from '../deepomaticmodal/DeepomaticModal';

interface IUploadImageResponse {
    issues: IDeepomaticIssueOrTask[];
    tasksWithOcr: IDeepomaticTaskWithOcr[];
    tasks: IDeepomaticIssueOrTask[];
    cameraConnectorLink: string;
}

interface IDeepomaticTaskToFormName {
    taskName: string;
    fieldName: string;
    fieldDisplayName: string;
}

interface IPhotoUploadProps {
    maxNumber: number;
    label: string;
    imageCode: ImageCategory;
    className?: string;
    mandatory?: boolean;
    controlType?: string;
    connectionType?: string;
    deepomaticWorkOrderId?: string | false | undefined;
    deepomaticTaskGroup?: string | false;
    deviceMacAddress?: string | false;
    formRef?: React.RefObject<FormikProps<DevicesUpdateFormValues>>;
    deepomaticTasksToFormNames?: IDeepomaticTaskToFormName[];
}

const EXCLUDED_PHOTOS_CATEGORIES = [
    ImageCategories.photosOther as string,
    ImageCategories.safetyRisksOrAnomalies as string,
];

const PhotoUpload: FC<IPhotoUploadProps> = ({
    maxNumber,
    imageCode,
    label,
    className,
    mandatory = false,
    controlType,
    connectionType,
    deviceMacAddress,
    deepomaticWorkOrderId,
    deepomaticTaskGroup,
    formRef,
    deepomaticTasksToFormNames,
}) => {
    maxNumber = 10;
    const [presentToast, dismiss] = useIonToast();
    const [images, setImages] = useState<ImageListType>([]);
    const [loading, setLoading] = useState(false);
    const [accessToken] = useAtom(accessTokenAtom);
    const [jobId] = useAtom(jobIdAtom);
    const [value, refreshPhotos] = useAtom(photosListLoadableAtom);
    const [, setPhotosUploaded] = useAtom(photosUploadedAtoms);
    const [deepomaticModalIsOpen, setDeepomaticModalIsOpen] = useState(false);
    const [deepomaticIssues, setDeepomaticIssues] = useState<
        IDeepomaticIssueOrTask[]
    >([]);
    const [deepomaticCameraLink, setDeepomaticCameraLink] = useState('');
    const [deepomaticTasksWithOCR, setDeepomaticTasksWithOCR] = useState<
        IDeepomaticTaskNameAndValueForm[]
    >([]);
    const [deepomaticTasks, setDeepomaticTasks] = useState<
        IDeepomaticIssueOrTask[]
    >([]);
    const interventionImages: string[] = useMemo(
        () => (value.state === 'hasData' ? value.data : []),
        [value]
    );

    useEffect(() => {
        if (!EXCLUDED_PHOTOS_CATEGORIES.includes(imageCode)) {
            setPhotosUploaded((photosUploaded) => ({
                ...photosUploaded,
                [imageCode]: {
                    mandatory,
                    uploaded: images.length > 0,
                },
            }));
        }
    }, [imageCode, images.length, mandatory, setPhotosUploaded]);

    useIonViewDidEnter(() => {
        if (!EXCLUDED_PHOTOS_CATEGORIES.includes(imageCode)) {
            setPhotosUploaded((photosUploaded) => ({
                ...photosUploaded,
                [imageCode]: {
                    mandatory,
                    uploaded: images.length > 0,
                },
            }));
        }
    }, [imageCode, images.length, mandatory, setPhotosUploaded]);

    useEffect(() => {
        (async () => {
            let getImageList: string[] = [];

            const imageRegex = new RegExp('_' + imageCode + '_');

            getImageList = interventionImages
                .filter((image: string) => {
                    const regexTest = image.match(imageRegex);
                    if (regexTest && regexTest.length > 0) {
                        return image;
                    }

                    return false;
                })
                .sort();

            if (
                getImageList.length > 0 &&
                images.length !== getImageList.length
            ) {
                // TODO: handle errors
                const getImages = getImageList.map((image) => {
                    return fetch(`${ROOT_URL}${image}`, {
                        credentials: 'include',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    });
                });

                setLoading(true);
                const getImagesResponse = await Promise.all(getImages);

                let photos = [];

                for (const image of getImagesResponse) {
                    const blob = await image.blob();
                    const objectUrl = URL.createObjectURL(blob);
                    const file: ImageType = {
                        dataURL: objectUrl,
                    };
                    photos.push(file);
                }

                setImages(photos as never[]);
                setLoading(false);
            }
        })();
    }, [accessToken, imageCode, images.length, interventionImages]);

    const compressFile = async (file: File) => {
        const options = {
            maxSizeMB: 3.5,
            maxWidthOrHeight: 1000,
            preserveExif: true,
        };
        try {
            return await imageCompression(file, options);
        } catch (error) {
            console.log('Error while compressing file: ', error);
            return false;
        }
    };

    const dataURLFromFile = (file: File) => {
        return new Promise<string | ArrayBuffer | null | undefined>(
            (resolve) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    resolve(event.target?.result);
                };
                reader.readAsDataURL(file);
            }
        );
    };

    const parseTasksWithOcr = (tasksWithOcr: IDeepomaticTaskWithOcr[]) => {
        const parsedTasksWithOcr: IDeepomaticTaskNameAndValueForm[] = [];
        tasksWithOcr.forEach((t) => {
            const d = deepomaticTasksToFormNames?.find(
                (d) => d.taskName === t.name
            );
            const taskValueAsInteger = parseInt(t.value);
            const taskDisplayValueAsInteger = parseInt(t.displayValue);
            if (d)
                parsedTasksWithOcr.push({
                    fieldName: d.fieldName,
                    fieldDisplayName: d.fieldDisplayName,
                    value: Number.isNaN(taskValueAsInteger)
                        ? t.value
                        : taskValueAsInteger,
                    displayValue: Number.isNaN(taskDisplayValueAsInteger)
                        ? t.displayValue
                        : taskDisplayValueAsInteger,
                });
        });
        return parsedTasksWithOcr;
    };

    const onChange = async (
        imageList: ImageListType,
        addUpdateIndex: number[] | undefined
    ) => {
        presentToast({
            message: t`Uploading image...`,
            color: 'info',
        });
        const lastUpdateIndexExists =
            addUpdateIndex && addUpdateIndex[0] !== undefined;
        try {
            if (lastUpdateIndexExists) {
                const lastImageUpdated = imageList[addUpdateIndex[0]];
                let { dataURL: originalDataURL, file } = lastImageUpdated;

                let dataURL: string | ArrayBuffer | null | undefined =
                    originalDataURL;
                if (
                    file?.type === 'image/heif' ||
                    file?.type === 'image/heic'
                ) {
                    file = await convertHeicToJpeg(file);
                    const dataJpegUrl = await dataURLFromFile(file);
                    const jpgImage: ImageType = {
                        dataURL: dataJpegUrl as string,
                    };
                    imageList[addUpdateIndex[0]] = jpgImage;
                } else if (file?.type === 'image/png') {
                    file = await convertPngToJpeg(file);
                }

                const compressedFile = await compressFile(file as File);
                if (compressedFile) {
                    const compressedDataURL =
                        await dataURLFromFile(compressedFile);
                    if (compressedDataURL) {
                        dataURL = compressedDataURL;
                    }
                }

                if (!dataURL) {
                    throw new Error('No image data');
                }
                const imageType =
                    file?.type && file.type === 'image/png' ? 'png' : 'jpg';

                const imageInfo: ImageInfo = {
                    typepic: imageCode,
                    typefic: imageType,
                    dataUri: dataURL as string,
                };
                const deepomaticInfo = {
                    deepomaticWorkOrderId,
                    deepomaticTaskGroup,
                    deviceMacAddress,
                    controlType,
                    connectionType,
                };
                Object.entries(deepomaticInfo).forEach(([k, v]) => {
                    if (v) {
                        Object.assign(imageInfo, { [`${k}`]: v });
                    }
                });
                const uploadImageResponse: IUploadImageResponse =
                    await uploadImageRequest(accessToken, jobId, imageInfo);
                const { issues, tasks, tasksWithOcr, cameraConnectorLink } =
                    uploadImageResponse;
                await refreshPhotos();

                const parsedTasksWithOcr = parseTasksWithOcr(tasksWithOcr);
                setDeepomaticTasksWithOCR(parsedTasksWithOcr);
                setDeepomaticIssues(issues);
                setDeepomaticTasks(tasks);
                setDeepomaticCameraLink(cameraConnectorLink);
                if ([issues, tasks, tasksWithOcr].some((e) => e.length > 0)) {
                    setDeepomaticModalIsOpen(true);
                }
            }
            setImages(imageList);
            await dismiss();
        } catch (e: any) {
            setImages((prevState) => {
                if (prevState) {
                    if (lastUpdateIndexExists) {
                        prevState.splice(addUpdateIndex[0], 1);
                    }
                }
                return prevState;
            });
            await dismiss();
            await presentToast({
                message: getErrorMessage(
                    e,
                    t`Oops, something went wrong while uploading image`
                ),
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
        setLoading(false);
    };

    if (value.state === 'loading' || loading) {
        return (
            <IonRow className="ion-justify-content-center ion-align-items-center ion-padding">
                <IonSpinner name="crescent" className="ion-margin-end" />
                <IonLabel>
                    <Trans>Loading Images</Trans>
                </IonLabel>
            </IonRow>
        );
    }

    return (
        <div className={className}>
            <DeepomaticModal
                isOpen={deepomaticModalIsOpen}
                deepomaticIssues={deepomaticIssues}
                deepomaticTasks={deepomaticTasks}
                deepomaticCameraLink={deepomaticCameraLink}
                deepomaticTasksWithOCR={deepomaticTasksWithOCR}
                formRef={formRef}
                closeModal={() => setDeepomaticModalIsOpen(false)}
            ></DeepomaticModal>
            <IonRow
                className="ion-justify-content-between ion-align-items-center"
                style={{ marginBottom: '10px' }}
            >
                <IonCol>
                    <IonLabel className="no-overflow">{label}</IonLabel>
                </IonCol>
                <IonCol size="auto">
                    <ImageUploading
                        multiple
                        value={images}
                        onChange={onChange}
                        maxNumber={maxNumber}
                        allowNonImageType={false}
                        acceptType={[
                            'png',
                            'jpeg',
                            'jpg',
                            'heic',
                            'android/force-camera-workaround',
                        ]}
                    >
                        {({
                            imageList,
                            onImageUpload,
                            onImageRemoveAll,
                            onImageUpdate,
                            onImageRemove,
                            isDragging,
                            dragProps,
                        }) => (
                            <div className="upload__image-wrapper">
                                <IonButton
                                    size="small"
                                    disabled={
                                        loading || images.length >= maxNumber
                                    }
                                    style={
                                        isDragging
                                            ? { color: 'red' }
                                            : undefined
                                    }
                                    onClick={onImageUpload}
                                    {...dragProps}
                                >
                                    <Trans>+ Photo</Trans>
                                </IonButton>
                            </div>
                        )}
                    </ImageUploading>
                </IonCol>
            </IonRow>
            <Swiper modules={[Pagination]} pagination={{ clickable: true }}>
                {images.map((image, index) => (
                    <SwiperSlide key={index}>
                        {/* <IonImg
                            src={image.dataURL}
                            onIonImgDidLoad={() => {
                                image.dataURL &&
                                    URL.revokeObjectURL(image.dataURL);
                            }}
                        /> */}
                        <img
                            // loading="lazy"
                            src={image.dataURL}
                            onLoad={() => {
                                image.dataURL &&
                                    URL.revokeObjectURL(image.dataURL);
                            }}
                            alt=""
                        />
                        {/* <div className="swiper-lazy-preloader"></div> */}
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

export default PhotoUpload;
